import {BlockTypeEnum, FlashSale as FlashSaleType, FlashSaleProduct, ProductPageType} from '__generated__/__types__';
import {
    useBestsellerProductsQuery,
    useHighlightProductsHotNewItemsLazyQuery,
    useHighlightProductsLazyQuery,
    useNewArrivalProductsQuery,
    useHighlightProductBlocksQuery,
    useHighlightProductsPageQuery,
    useHighlightProductsPageLazyQuery,
    useFlashSaleProductsWithPaginationQuery,
} from '@/framework/gql/products-promotion.graphql';
import {NetworkStatus} from '@apollo/client';
import {PRODUCTS_PER_PAGE} from '@/framework/client/variables';

export function useFlashSalesProducts({
    currentPage = 1,
    pageSize = PRODUCTS_PER_PAGE,
}: {
    currentPage?: number;
    pageSize?: number;
}) {
    const {
        data,
        loading: isLoading,
        error,
        fetchMore,
    } = useFlashSaleProductsWithPaginationQuery({
        variables: {
            currentPage,
            pageSize,
        },
        notifyOnNetworkStatusChange: true,
    });
    const flashSaleInfo = data?.flashSaleProductsV2?.flash_sale;
    const flashSaleData = data?.flashSaleProductsV2?.flash_sale_items?.items?.filter(item => item?.product) ?? [];
    const pageInfo = data?.flashSaleProductsV2?.flash_sale_items?.page_info;
    const totalCount = data?.flashSaleProductsV2?.flash_sale_items?.total_count ?? 0;

    const isFlashSaleValid = (flashSaleInfo: FlashSaleType) => {
        if (!flashSaleInfo) {
            return false;
        }
        const currentDate = new Date();
        const saleStartDate = new Date(flashSaleInfo.date_from!);
        const saleEndDate = new Date(flashSaleInfo.date_to!);
        return currentDate.getTime() >= saleStartDate.getTime() && currentDate.getTime() <= saleEndDate.getTime();
    };

    const isShowFlashSale =
        !isLoading && !!flashSaleInfo && isFlashSaleValid(flashSaleInfo) && flashSaleData.length > 0;

    const handleLoadMore = () => {
        const {current_page, total_pages} = pageInfo ?? {};
        if (!current_page || !total_pages) return;
        if (current_page < total_pages) {
            fetchMore({
                variables: {
                    currentPage: (pageInfo?.current_page ?? 0) + 1,
                },
                updateQuery: (prev, {fetchMoreResult}) => {
                    if (!fetchMoreResult) return prev;
                    return fetchMoreResult;
                },
            });
        }
    };

    return {
        items: (flashSaleData ?? []) as FlashSaleProduct[],
        flashSaleInfo: data?.flashSaleProductsV2?.flash_sale,
        handleLoadMore,
        pageInfo,
        totalCount,
        isShowFlashSale,
        isLoading,
        error,
    };
}

export function useBestsellerProducts() {
    const {data, loading: isLoading, error} = useBestsellerProductsQuery();

    return {
        products: data?.bestsellerProducts?.items ?? [],
        isLoading,
        error,
    };
}

export function useHighlightProducts() {
    const [fetchHighlightProducts, {data, loading: isLoading, error}] = useHighlightProductsLazyQuery({
        fetchPolicy: 'no-cache',
    });

    return {
        fetchHighlightProducts,
        highlightProducts: data?.highlightProducts?.highlight_items ?? [],
        isLoading,
        error,
    };
}

export function useHighlightProductsHotNewItems() {
    const [fetchHighlightProductsHotNewItems, {data, loading: isLoading, error}] =
        useHighlightProductsHotNewItemsLazyQuery({
            fetchPolicy: 'no-cache',
        });

    return {
        fetchHighlightProductsHotNewItems,
        hotNewProducts: data?.highlightProducts?.hot_new_items ?? [],
        isLoading,
        error,
    };
}

export function useNewArrivalProducts() {
    const {data, loading: isLoading, error} = useNewArrivalProductsQuery();

    return {
        newArrivalProducts: data?.newArrivalProducts?.items ?? [],
        isLoading,
        error,
    };
}

export const useHighlightProductBlocks = ({blockType}: {blockType: BlockTypeEnum}) => {
    const {data, loading, error} = useHighlightProductBlocksQuery({variables: {blockTypes: blockType}});

    return {
        highlightProducts: data?.highlightProductBlocks?.items?.at(0)?.highlight_items ?? [],
        isLoading: loading,
        error,
    };
};

export const useHighlightProductPage = ({
    type,
    pageSize,
    currentPage,
}: {
    type: ProductPageType;
    pageSize: number;
    currentPage: number;
}) => {
    const {data, loading, error} = useHighlightProductsPageQuery({
        variables: {type: type, pageSize: pageSize, currentPage: currentPage},
    });

    return {
        hotNewProducts: data?.highlightProductsPage ?? {},
        isLoading: loading,
        error,
    };
};

export const useHighlightProductPageLazy = ({
    type,
    pageSize,
    currentPage,
}: {
    type: ProductPageType;
    pageSize: number;
    currentPage: number;
}) => {
    const [fethchHighlightProductPageLazy, {data, loading: isLoading, error, fetchMore, networkStatus}] =
        useHighlightProductsPageLazyQuery({
            notifyOnNetworkStatusChange: true,
            variables: {
                type: type,
                pageSize: pageSize,
                currentPage: currentPage,
            },
        });

    function handleLoadMore() {
        if (
            data?.highlightProductsPage?.page_info?.current_page! < data?.highlightProductsPage?.page_info?.total_pages!
        ) {
            fetchMore({
                variables: {
                    currentPage: data?.highlightProductsPage?.page_info?.current_page ?? currentPage,
                    type: type,
                    pageSize: data?.highlightProductsPage?.page_info?.page_size
                        ? data?.highlightProductsPage?.page_info?.page_size + 20
                        : pageSize + 20,
                },
                updateQuery: (prev, {fetchMoreResult}) => {
                    if (!fetchMoreResult) return prev;
                    return fetchMoreResult;
                },
            });
        }
    }
    const fetchData = async ({
        type,
        pageSize,
        currentPage,
    }: {
        type: ProductPageType;
        pageSize: number;
        currentPage: number;
    }) => {
        const response = await fethchHighlightProductPageLazy({
            variables: {
                type: type,
                pageSize: pageSize,
                currentPage: currentPage,
            },
        });
        return response.data?.highlightProductsPage;
    };

    return {
        fetchData,
        hotNewProducts: data?.highlightProductsPage ?? {},
        isLoading,
        error,
        isLoadingMore: networkStatus === NetworkStatus.fetchMore,
        loadMore: handleLoadMore,
        hasMore: Boolean(
            data?.highlightProductsPage?.page_info?.current_page! <
                data?.highlightProductsPage?.page_info?.total_pages!,
        ),
        pageInfo: data?.highlightProductsPage?.page_info ?? {},
        totalCount: data?.highlightProductsPage?.total_count ?? 0,
    };
};
